import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSocket } from '../store/SocketContext';
import { API_PATH, request } from '../config/api';
import { useConfirm } from '../store/ConfirmDialogContext';

const API_BASE = API_PATH;

export function useEventPage(eventId, options = {}) {
    const { onPollCreated } = options;
    const [event, setEvent] = useState(null);
    const [activities, setActivities] = useState([]);
    const [activeActivityId, setActiveActivityId] = useState(() => {
        const saved = localStorage.getItem(`lp_active_activity_id_${eventId}`);
        return saved ? JSON.parse(saved) : null;
    });
    const [isFetching, setIsFetching] = useState(true);
    const [fetchError, setFetchError] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [isLaunchingId, setIsLaunchingId] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem(`lp_interactions_collapsed_${eventId}`);
        return saved ? JSON.parse(saved) : false;
    });
    const [launchedActivityId, setLaunchedActivityId] = useState(null);
    const [shareNote, setShareNote] = useState('');
    const [addActivityError, setAddActivityError] = useState('');
    const shareNoteTimer = useRef(null);
    const socket = useSocket();
    const confirm = useConfirm();

    useEffect(() => {
        localStorage.setItem(`lp_interactions_collapsed_${eventId}`, JSON.stringify(isSidebarCollapsed));
    }, [isSidebarCollapsed, eventId]);

    useEffect(() => {
        if (activeActivityId) {
            localStorage.setItem(`lp_active_activity_id_${eventId}`, JSON.stringify(activeActivityId));
        }
    }, [activeActivityId, eventId]);

    useEffect(() => {
        const controller = new AbortController();
        setFetchError('');
        const load = async () => {
            try {
                const [evRes, acRes] = await Promise.all([
                    request(`${API_BASE}/events`, { signal: controller.signal }),
                    request(`${API_BASE}/events/${eventId}/activities`, { signal: controller.signal }),
                ]);
                if (!evRes.ok) {
                    const evData = await evRes.json().catch(() => ({}));
                    const msg = evRes.status === 403 && evData?.error ? evData.error : 'Failed to load event';
                    throw new Error(msg);
                }
                if (!acRes.ok) {
                    const acData = await acRes.json().catch(() => ({}));
                    const msg = acRes.status === 403 && acData?.error ? acData.error : 'Failed to load activities';
                    throw new Error(msg);
                }
                const events = await evRes.json();
                const ev = events.find((e) => String(e.id) === String(eventId));
                setEvent(ev || null);
                if (ev?.active_activity_id) setLaunchedActivityId(ev.active_activity_id);
                const data = await acRes.json();
                const list = Array.isArray(data) ? data : [];
                const savedOrderKey = `lp_activity_order_${eventId}`;
                let ordered = list;
                try {
                    const saved = localStorage.getItem(savedOrderKey);
                    const orderIds = saved ? JSON.parse(saved) : null;
                    if (Array.isArray(orderIds) && orderIds.length > 0) {
                        const byId = new Map(list.map((a) => [Number(a.id), a]));
                        const orderedList = [];
                        for (const id of orderIds) {
                            const a = byId.get(Number(id));
                            if (a) {
                                orderedList.push(a);
                                byId.delete(Number(id));
                            }
                        }
                        byId.forEach((a) => orderedList.push(a));
                        ordered = orderedList;
                    }
                } catch (_) {}
                setActivities(ordered);
                if (ordered.length > 0) setActiveActivityId((prev) => prev ?? ordered[0].id);
            } catch (err) {
                if (err.name === 'AbortError') return;
                setFetchError(err.message || 'Failed to load event');
            } finally {
                setIsFetching(false);
            }
        };
        load();
        return () => controller.abort();
    }, [eventId]);

    useEffect(() => {
        if (!socket || !eventId) return;
        socket.emit('admin:join', { eventId });
        const handleNewResponse = ({ activityId, isNewRecord }) => {
            if (isNewRecord === false && isNewRecord !== undefined) return;
            setActivities((prev) =>
                prev.map((a) => (a.id === Number(activityId) ? { ...a, response_count: (a.response_count || 0) + 1 } : a))
            );
        };
        const handleResultsCleared = ({ activityId }) => {
            setActivities((prev) =>
                prev.map((a) => (a.id === Number(activityId) ? { ...a, response_count: 0 } : a))
            );
        };
        const handleActivityUpdated = ({ activity: updated }) => {
            if (!updated?.id) return;
            setActivities((prev) =>
                prev.map((a) => (a.id === Number(updated.id) ? { ...a, ...updated } : a))
            );
        };
        const handleActivityStopped = () => {
            setLaunchedActivityId(null);
        };
        // Keeps a second admin tab (or a second admin) in sync when this activity is launched
        // from elsewhere — this tab previously only heard about stops, never launches, so its
        // own "Live" badge could go stale until a manual reload.
        const handleActivityLaunchedSocket = ({ activity: launched }) => {
            if (!launched?.id) return;
            setLaunchedActivityId(launched.id);
        };
        // Someone (possibly another admin tab) deleted an activity — drop it locally and, if it
        // was the one currently live, clear that state too.
        const handleActivityDeleted = ({ activityId }) => {
            const id = Number(activityId);
            let remaining = null;
            setActivities((prev) => {
                remaining = prev.filter((a) => a.id !== id);
                return remaining;
            });
            setActiveActivityId((prev) => (prev === id ? (remaining?.[0]?.id ?? null) : prev));
            setLaunchedActivityId((prev) => (prev === id ? null : prev));
        };
        // Reconnect resync: a socket drop can land at the exact moment another admin launches,
        // stops, or deletes an activity, or a participant responds — those broadcasts are never
        // replayed. Re-fetch on every (re)connect so response counts and the live-poll state
        // can't silently drift until a full page reload. Only merges response_count into the
        // existing, locally-ordered list rather than replacing it outright.
        const handleConnect = () => {
            socket.emit('admin:join', { eventId });
            request(`${API_BASE}/events`)
                .then((r) => (r.ok ? r.json() : null))
                .then((events) => {
                    if (!Array.isArray(events)) return;
                    const ev = events.find((e) => String(e.id) === String(eventId));
                    if (ev) setLaunchedActivityId(ev.active_activity_id || null);
                })
                .catch(() => {});
            request(`${API_BASE}/events/${eventId}/activities`)
                .then((r) => (r.ok ? r.json() : null))
                .then((fresh) => {
                    if (!Array.isArray(fresh)) return;
                    const byId = new Map(fresh.map((a) => [Number(a.id), a]));
                    setActivities((prev) =>
                        prev.map((a) => {
                            const match = byId.get(Number(a.id));
                            return match ? { ...a, response_count: match.response_count } : a;
                        })
                    );
                })
                .catch(() => {});
        };
        socket.on('response:new', handleNewResponse);
        socket.on('results:cleared', handleResultsCleared);
        socket.on('activity:updated', handleActivityUpdated);
        socket.on('activity:stopped', handleActivityStopped);
        socket.on('activity:launched', handleActivityLaunchedSocket);
        socket.on('activity:deleted', handleActivityDeleted);
        socket.on('connect', handleConnect);
        return () => {
            socket.off('response:new', handleNewResponse);
            socket.off('results:cleared', handleResultsCleared);
            socket.off('activity:updated', handleActivityUpdated);
            socket.off('activity:stopped', handleActivityStopped);
            socket.off('activity:launched', handleActivityLaunchedSocket);
            socket.off('activity:deleted', handleActivityDeleted);
            socket.off('connect', handleConnect);
        };
    }, [socket, eventId]);

    useEffect(() => {
        const handler = () => setOpenDropdownId(null);
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, []);

    useEffect(() => () => { if (shareNoteTimer.current) clearTimeout(shareNoteTimer.current); }, []);

    const showShareNote = useCallback((msg) => {
        setShareNote(msg);
        if (shareNoteTimer.current) clearTimeout(shareNoteTimer.current);
        shareNoteTimer.current = setTimeout(() => setShareNote(''), 1800);
    }, []);

    const apiCall = useCallback(
        async (path, options = {}) => {
            return request(`${API_BASE}${path}`, {
                ...options,
                headers: { 'Content-Type': 'application/json', ...options.headers },
            });
        },
        []
    );

    const handleActivitySelected = useCallback(
        async (activityType) => {
            setAddActivityError('');
            try {
                const res = await apiCall(`/events/${eventId}/activities`, {
                    method: 'POST',
                    body: JSON.stringify({ type: activityType.id, title: activityType.label }),
                });
                if (res.ok) {
                    const newActivity = await res.json();
                    setActivities((prev) => [...prev, newActivity]);
                    setActiveActivityId(newActivity.id);
                    if (typeof onPollCreated === 'function') onPollCreated();
                    try {
                        const key = `lp_activity_order_${eventId}`;
                        const saved = localStorage.getItem(key);
                        const orderIds = saved ? JSON.parse(saved) : [];
                        orderIds.push(newActivity.id);
                        localStorage.setItem(key, JSON.stringify(orderIds));
                    } catch (_) {}
                } else if (res.status === 402) {
                    const data = await res.json().catch(() => ({}));
                    setAddActivityError(data.error || 'Insufficient credits. Redeem a coupon in Settings.');
                }
            } catch (err) {
                console.error(err);
                setAddActivityError('Failed to create poll. Try again.');
            }
        },
        [eventId, apiCall, onPollCreated]
    );

    const handleDuplicateActivity = useCallback(
        async (activityId) => {
            const activity = activities.find((a) => a.id === activityId);
            if (!activity) return;
            setAddActivityError('');
            try {
                const title = activity.title || 'Poll';
                const res = await apiCall(`/events/${eventId}/activities`, {
                    method: 'POST',
                    body: JSON.stringify({ type: activity.type, title }),
                });
                if (!res.ok) {
                    if (res.status === 402) {
                        const data = await res.json().catch(() => ({}));
                        setAddActivityError(data.error || 'Insufficient credits. Redeem a coupon in Settings.');
                    } else {
                        const data = await res.json().catch(() => ({}));
                        setAddActivityError(data.error || 'Failed to duplicate poll. Try again.');
                    }
                    return;
                }
                const newActivity = await res.json();
                const content =
                    activity.content && typeof activity.content === 'object'
                        ? JSON.parse(JSON.stringify(activity.content))
                        : null;
                if (content) {
                    const patchRes = await apiCall(`/events/${eventId}/activities/${newActivity.id}`, {
                        method: 'PATCH',
                        body: JSON.stringify({ content }),
                    });
                    if (patchRes.ok) {
                        const updated = await patchRes.json();
                        Object.assign(newActivity, updated);
                    }
                }
                setActivities((prev) => [...prev, newActivity]);
                setActiveActivityId(newActivity.id);
                if (typeof onPollCreated === 'function') onPollCreated();
                try {
                    const key = `lp_activity_order_${eventId}`;
                    const saved = localStorage.getItem(key);
                    const orderIds = saved ? JSON.parse(saved) : [];
                    orderIds.push(newActivity.id);
                    localStorage.setItem(key, JSON.stringify(orderIds));
                } catch (_) {}
            } catch (err) {
                console.error(err);
                setAddActivityError('Failed to duplicate poll. Try again.');
            }
        },
        [eventId, activities, apiCall, onPollCreated]
    );

    const handleDeleteActivity = useCallback(
        async (activityId, e) => {
            e?.stopPropagation();
            const activity = activities.find((a) => a.id === activityId);
            const responseCount = activity?.response_count || 0;
            const activityTitle = activity?.title || 'this activity';

            const details = responseCount > 0
                ? React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(
                        'span',
                        { style: { fontWeight: 600 } },
                        `⚠️ This activity has ${responseCount} response${responseCount !== 1 ? 's' : ''}.`
                    ),
                    React.createElement('br', null),
                    'Consider exporting your results before deleting — ',
                    React.createElement('strong', null, 'this data cannot be recovered'),
                    ' once deleted.'
                )
                : undefined;

            const ok = await confirm({
                title: 'Delete Activity',
                message: `Are you sure you want to delete "${activityTitle}"? This action cannot be undone.`,
                details,
                confirmText: 'Delete',
                tone: 'danger',
            });
            if (!ok) return;
            setDeletingId(activityId);
            try {
                const res = await request(`${API_BASE}/events/${eventId}/activities/${activityId}`, {
                    method: 'DELETE',
                });
                if (res.ok) {
                    setActivities((prev) => {
                        const next = prev.filter((a) => a.id !== activityId);
                        if (activeActivityId === activityId) setActiveActivityId(next[0]?.id ?? null);
                        return next;
                    });
                    setLaunchedActivityId((prev) => (prev === activityId ? null : prev));
                    try {
                        const key = `lp_activity_order_${eventId}`;
                        const saved = localStorage.getItem(key);
                        if (saved) {
                            const orderIds = JSON.parse(saved).filter((id) => Number(id) !== Number(activityId));
                            localStorage.setItem(key, JSON.stringify(orderIds));
                        }
                    } catch (_) {}
                } else {
                    const data = await res.json().catch(() => ({}));
                    alert(data.error || 'Failed to delete poll. Try again.');
                }
            } catch (err) {
                console.error(err);
                alert('Failed to delete poll. Try again.');
            } finally {
                setDeletingId(null);
            }
        },
        [eventId, activeActivityId, activities, confirm]
    );

    const handleLaunchActivity = useCallback(
        async (activityId, e) => {
            e?.stopPropagation();
            try {
                setIsLaunchingId(activityId);
                const res = await request(`${API_BASE}/events/${eventId}/launch/${activityId}`, {
                    method: 'POST',
                });
                if (!res.ok) {
                    const data = await res.json();
                    throw new Error(data.error || 'Launch failed');
                }
                setLaunchedActivityId(activityId);
                setActiveActivityId(activityId);
            } catch (err) {
                alert(`Launch failed: ${err.message}`);
            } finally {
                setIsLaunchingId(null);
            }
        },
        [eventId]
    );

    const handleStopActivity = useCallback(
        async (activityId, e) => {
            e?.stopPropagation();
            try {
                const res = await request(`${API_BASE}/events/${eventId}/stop`, {
                    method: 'POST',
                });
                if (!res.ok) {
                    const data = await res.json();
                    throw new Error(data.error || 'Stop failed');
                }
                setLaunchedActivityId((prev) => (prev === activityId ? null : prev));
            } catch (err) {
                alert(`Stop failed: ${err.message}`);
            }
        },
        [eventId]
    );

    const handleUpdateActivitySetting = useCallback(
        async (activityId, key, value) => {
            const activity = activities.find((a) => a.id === activityId);
            if (!activity) return;
            const newContent = {
                ...(activity.content || {}),
                settings: { ...(activity.content?.settings || {}), [key]: value },
            };
            try {
                const res = await apiCall(`/events/${eventId}/activities/${activityId}`, {
                    method: 'PATCH',
                    body: JSON.stringify({ content: newContent }),
                });
                if (res.ok) {
                    const updated = await res.json();
                    setActivities((prev) => prev.map((a) => (a.id === activityId ? updated : a)));
                }
            } catch (err) {
                console.error('Failed to update setting:', err);
            }
        },
        [eventId, activities, apiCall]
    );

    const handleResetResults = useCallback(
        async (activityId) => {
            const ok = await confirm({
                title: 'Reset results',
                message: 'Clear all responses to reuse this poll? This will deduct the same number of coins as creating a new poll (unless you have an active subscription). You can export your data first if needed.',
                confirmText: 'Reset (deduct coins)',
                tone: 'danger',
            });
            if (!ok) return;
            try {
                const res = await request(`${API_BASE}/events/${eventId}/activities/${activityId}/results`, {
                    method: 'DELETE',
                });
                const data = await res.json().catch(() => ({}));
                if (res.ok) {
                    alert('Results cleared. Coins were deducted for reusing this poll (check Coin history in Settings if you use coins).');
                    setActivities((prev) => [...prev]);
                } else {
                    alert(data.error || 'Cannot clear results');
                }
            } catch (err) {
                console.error(err);
                alert('Failed to clear results');
            }
        },
        [eventId, confirm]
    );

    const handleCopyShare = useCallback(
        async (text, label) => {
            if (!text) return;
            try {
                await navigator.clipboard.writeText(text);
                showShareNote(`${label} copied`);
            } catch {
                window.prompt('Copy this value', text);
            }
        },
        [showShareNote]
    );

    const handleCopyDirectLink = useCallback(
        (activityId) => {
            const href = `${window.location.origin}/events/${eventId}#activity-${activityId}`;
            navigator.clipboard.writeText(href).then(() => alert('Direct link copied')).catch(() => window.prompt('Copy this link', href));
        },
        [eventId]
    );

    const handleActivitySettingsChange = useCallback((activityId, settings) => {
        setActivities((prev) =>
            prev.map((a) =>
                a.id === Number(activityId)
                    ? { ...a, content: { ...(a.content || {}), settings: { ...(a.content?.settings || {}), ...(settings || {}) } } }
                    : a
            )
        );
    }, []);

    const handleKeySelect = useCallback((e, id) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setActiveActivityId(id);
        }
    }, []);

    const handleReorderActivities = useCallback(
        async (newOrderedActivities) => {
            if (!Array.isArray(newOrderedActivities) || newOrderedActivities.length === 0) return;
            const orderIds = newOrderedActivities.map((a) => a.id);
            setActivities(newOrderedActivities);
            try {
                localStorage.setItem(`lp_activity_order_${eventId}`, JSON.stringify(orderIds));
            } catch (_) {}
            try {
                const res = await request(`${API_BASE}/events/${eventId}/activities/reorder`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ order: orderIds }),
                });
                if (!res.ok) {
                    const data = await res.json().catch(() => ({}));
                    console.error('Reorder persist failed:', data.error);
                    alert(data.error || 'Failed to save the new poll order. It may not survive a reload.');
                }
            } catch (err) {
                console.error('Reorder persist failed:', err);
                alert('Failed to save the new poll order. It may not survive a reload.');
            }
        },
        [eventId]
    );

    const activeActivity = useMemo(() => activities.find((a) => a.id === activeActivityId), [activities, activeActivityId]);
    const joinUrl = useMemo(() => (event?.code ? `${window.location.origin}/join/${event.code}` : ''), [event]);

    return {
        event,
        activities,
        activeActivityId,
        setActiveActivityId,
        activeActivity,
        isFetching,
        fetchError,
        addActivityError,
        setAddActivityError,
        deletingId,
        openDropdownId,
        setOpenDropdownId,
        isLaunchingId,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        launchedActivityId,
        shareNote,
        joinUrl,
        showShareNote,
        handleActivitySelected,
        handleDuplicateActivity,
        handleDeleteActivity,
        handleLaunchActivity,
        handleStopActivity,
        handleUpdateActivitySetting,
        handleResetResults,
        handleCopyShare,
        handleCopyDirectLink,
        handleActivitySettingsChange,
        handleKeySelect,
        handleReorderActivities,
    };
}
